import localeConfig from '~/config/locale-config'

const organizationEnvironmentMap = {
  f_ecom_aanc_dev: 'dev',
  f_ecom_aanc_001: 'dev2',
} satisfies Record<string, 'dev' | 'dev2'>

const siteOriginMap = {
  MichaelHillAU: 'michaelhill.com.au',
  MichaelHillCA: 'michaelhill.ca',
  MichaelHillNZ: 'michaelhill.co.nz',
} satisfies Record<string, string>

type LocalDevSfccFallback = {
  siteId?: string | null
  origin?: string | null
}

const normalizeHost = (host?: string | null) => {
  const value = host?.split(',')[0]?.trim()
  if (!value) return

  try {
    return new URL(value).hostname.toLowerCase()
  } catch {
    return value.split(':')[0].toLowerCase()
  }
}

const resolveByHost = (host?: string | null, organizationId?: string | null) => {
  const hostname = normalizeHost(host)
  const environment = organizationEnvironmentMap[organizationId || '']
  if (!hostname || !environment) return

  const localeEntry = Object.values(localeConfig).find((entry) => normalizeHost(entry.domain) === hostname)
  const originHost = localeEntry?.siteId ? siteOriginMap[localeEntry.siteId] : undefined
  if (!localeEntry?.siteId || !originHost) return

  return {
    siteId: localeEntry.siteId,
    origin: `https://${environment}.${originHost}`,
  }
}

// Resolves the SFCC siteId + origin for a single dev request.
//
// 1. Tries host -> locale-config matching first. This is the portless flow (`pnpm dev`):
//    each country runs on its own subdomain (au./ca./nz.<parent>.localhost) and the host
//    header identifies the country.
// 2. Falls back to the explicit `fallback` values when no host match is found. This is the
//    `pnpm dev:direct` flow: no portless proxy, a single dev server pinned to one country
//    via `SFCC_SITE_ID` / `SFCC_ORIGIN` env vars.
//
// Gated on `import.meta.dev` — production has no notion of either of these flows.
export const getLocalDevSfccContext = (
  host?: string | null,
  organizationId?: string | null,
  fallback?: LocalDevSfccFallback,
): { siteId?: string; origin?: string } | undefined => {
  if (!import.meta.dev) return

  const hostBased = resolveByHost(host, organizationId)
  if (hostBased) return hostBased

  if (fallback?.siteId || fallback?.origin) {
    return {
      siteId: fallback.siteId ?? undefined,
      origin: fallback.origin ?? undefined,
    }
  }
}

export const resolveLocalDevSfccOrigin = (
  host?: string | null,
  organizationId?: string | null,
  fallback?: LocalDevSfccFallback,
) => {
  return getLocalDevSfccContext(host, organizationId, fallback)?.origin
}
